import React, { useState, useRef, useEffect } from "react";
import {
  ArrowLeft,
  MoreVertical,
  Smile,
  Paperclip,
  Send,
  Mic,
  Check,
  CheckCheck,
} from "lucide-react";
import "./ChatPage.css";
import WhatsAppText from "../../utilities/WhatsAppText";
import WebsocketService from "../../services/Websocket.service";

export default function AgentAssist() {
  const [message, setMessage] = useState("");
  const [messages, setMessages] = useState([]);
  const [isTyping, setIsTyping] = useState(false);

  const socketRef = useRef(null);
  const messagesEndRef = useRef(null);

  /* 🔽 Auto scroll */
  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  }, [messages, isTyping]);

  /* 🔥 WebSocket Connection (ONE TIME) */
  useEffect(() => {
    const ws = new WebSocket(WebsocketService.agentAssistChat());
    socketRef.current = ws;

    ws.onopen = () => console.log("🟢 WebSocket Connected");

    ws.onmessage = (event) => {
      const formatted = event.data.replace(/\n/g, "<br>");

      // show typing indicator
      setIsTyping(true);

      // WhatsApp-like delay (based on message length)
      const delay = Math.min(2000, formatted.length * 35);

      setTimeout(() => {
        const botMsg = {
          id: Date.now(),
          text: formatted,
          type: "received",
          time: new Date().toLocaleTimeString([], {
            hour: "2-digit",
            minute: "2-digit",
          }),
          seen: true,
        };

        setIsTyping(false);
        setMessages((prev) => [...prev, botMsg]);
      }, delay);
    };

    ws.onerror = (err) => console.error("WS Error:", err);
    ws.onclose = () => console.log("🔴 WebSocket Closed");

    return () => ws.close();
  }, []);

  /* 📤 Send Message */
  const sendMessage = () => {
    if (!message.trim()) return;

    const userMsg = {
      id: Date.now(),
      text: message,
      type: "sent",
      time: new Date().toLocaleTimeString([], {
        hour: "2-digit",
        minute: "2-digit",
      }),
      seen: false,
    };

    setMessages((prev) => [...prev, userMsg]);

    if (socketRef.current?.readyState === WebSocket.OPEN) {
      socketRef.current.send(message);
    }

    setMessage("");

    // fake delivery tick
    setTimeout(() => {
      setMessages((prev) =>
        prev.map((m) =>
          m.id === userMsg.id ? { ...m, seen: true } : m
        )
      );
    }, 800);
  };

  return (
    <div className="chat-container">
      {/* Header */}

      <div className="chat-header">
        <div style={{display:"flex", gap:"12px", alignItems:"center"}}>
        <ArrowLeft size={24} />
        <div className="profile-pic">AA</div>

        <div className="contact-info">
          <h3>Agent Assist</h3>
          <p style={{ color: "#959d95" }}>Online</p>
        </div>
        </div>
        <MoreVertical size={24} />
      </div>

      {/* Messages */}
      <div className="messages-area">
        {messages.map((msg) => (
          <div key={msg.id} className={`message ${msg.type}`}>
            <div className="message-bubble">
              <WhatsAppText text={msg.text} />

              <div className="message-time">
                {msg.time}
                {msg.type === "sent" && (
                  <span style={{ marginLeft: 6 }}>
                    {msg.seen ? (
                      <CheckCheck size={16} className="text-blue-400" />
                    ) : (
                      <Check size={16} className="text-gray-400" />
                    )}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}

        {/* Typing Indicator */}
        {isTyping && (
  <div className="message received">
    <div className="message-bubble typing-dots">
      <span></span>
      <span></span>
      <span></span>
    </div>
  </div>
)}


        <div ref={messagesEndRef} />
      </div>

      {/* Input */}
      <div className="input-area">
        <div className="input-wrapper">
          <Smile size={24} />
          <input
            type="text"
            placeholder="Type a message"
            value={message}
            onChange={(e) => setMessage(e.target.value)}
            onKeyDown={(e) => e.key === "Enter" && sendMessage()}
          />
          <Paperclip size={24} />
        </div>

        <div
          className={message.trim() ? "send-btn" : "mic-btn"}
          onClick={message.trim() ? sendMessage : () => {}}
        >
          {message.trim() ? <Send size={22} /> : <Mic size={24} />}
        </div>
      </div>
    </div>
  );
}
